import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

import careersSlide1 from "@/assets/careers/careers-slide-1.jpg";
import careersSlide2 from "@/assets/careers/careers-slide-2.jpg";
import careersSlide3 from "@/assets/careers/careers-slide-3.jpg";

const slides = [
  {
    title: "ÚNETE A NUESTRO EQUIPO",
    subtitle: "Forma parte de una empresa líder en servicios eléctricos con más de 15 años de experiencia y 200+ proyectos exitosos",
    cta: "VER VACANTES",
    ctaHref: "#formulario",
    image: careersSlide1,
  },
  {
    title: "CRECE PROFESIONALMENTE",
    subtitle: "Desarrollo continuo, capacitaciones y oportunidades de ascenso en un ambiente dinámico y colaborativo",
    cta: "POSTULAR AHORA",
    ctaHref: "#formulario",
    image: careersSlide2,
  },
  {
    title: "PROYECTOS DESAFIANTES",
    subtitle: "Trabaja en proyectos para clientes como Komatsu, DUOC UC, Mall Plaza, Claro Arena y muchos más",
    cta: "CONOCER MÁS",
    ctaHref: "#formulario",
    image: careersSlide3,
  },
];

const HeroCareers = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 7000, stopOnInteraction: false }),
  ]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", () => setCurrentSlide(emblaApi.selectedScrollSnap()));
  }, [emblaApi]);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative h-[70vh] min-h-[450px] max-h-[600px] overflow-hidden">
      {/* Carousel */}
      <div className="relative h-full" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, index) => (
            <div key={index} className="flex-[0_0_100%] min-w-0 relative">
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                />
                {/* Dark gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-dark-section/90 via-dark-section/70 to-dark-section/50" />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-section/80 via-transparent to-dark-section/40" />
              </div>

              {/* Content */}
              <div className="absolute inset-0 flex items-center">
                <div className="container mx-auto px-4">
                  <div className="max-w-2xl">
                    {/* Badge */}
                    <div className="inline-block px-4 py-2 glass-liquid-medium rounded-full mb-4">
                      <span className="text-primary font-inter font-semibold text-sm">
                        TRABAJA CON NOSOTROS
                      </span>
                    </div>

                    {/* Title */}
                    <h1 className="font-montserrat font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4 leading-tight">
                      {slide.title}
                    </h1>

                    {/* Subtitle */}
                    <p className="text-white/80 text-sm md:text-base lg:text-lg max-w-xl mb-6">
                      {slide.subtitle}
                    </p>

                    {/* CTA Button */}
                    <Button
                      onClick={() => scrollToSection(slide.ctaHref)}
                      className="glass-liquid-medium hover-liquid font-montserrat font-bold px-8 py-5 text-base rounded-full shadow-liquid-xl hover:shadow-liquid-2xl text-white bg-primary/80 hover:bg-primary/90"
                    >
                      {slide.cta}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={scrollPrev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 glass-liquid-dark-medium rounded-full flex items-center justify-center text-white hover:bg-primary/30 transition-all duration-300 z-20"
        aria-label="Slide anterior"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 glass-liquid-dark-medium rounded-full flex items-center justify-center text-white hover:bg-primary/30 transition-all duration-300 z-20"
        aria-label="Siguiente slide"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => emblaApi?.scrollTo(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              currentSlide === index
                ? "w-8 bg-primary"
                : "w-2 bg-white/40 hover:bg-white/60"
            }`}
            aria-label={`Ir a slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
        <div
          className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>
    </section>
  );
};

export default HeroCareers;
